/**
 * LeadVillaHoldCard — CRM-only temporary villa hold for a lead.
 *
 * Reads/writes a small, additive collection on the backend (villa_holds).
 * Does NOT touch villa.status, villa.owner_user_id, payments, contracts,
 * or investor portal visibility.
 *
 * UI behavior:
 *   - Lists holds tied to this lead (newest first), highlighting the
 *     active one if present.
 *   - When no active hold: form to pick a villa + duration (24h / 48h /
 *     72h / custom) + optional note, and a "Poner villa en hold" button.
 *   - When active hold present: villa name, "hold activo hasta {date}",
 *     time-remaining text, "Liberar hold" button.
 *   - On 409 villa-already-on-hold from another lead, surfaces a
 *     conflict message inline (no toast).
 *
 * Stable id contract: actions always target the prop `leadId` (not a
 * possibly stale `lead.lead_id`) so consecutive edits keep working —
 * mirrors the bugfix pattern from LeadSidePanel.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { House, Clock, Warning, X, Plus } from "@phosphor-icons/react";

const DURATION_PRESETS = [
  { key: "24h", label: "24h", hours: 24 },
  { key: "48h", label: "48h", hours: 48 },
  { key: "72h", label: "72h", hours: 72 },
  { key: "custom", label: "Personalizada", hours: null },
];

function toLocalDateTimeInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatRemaining(holdUntilIso) {
  try {
    const until = new Date(holdUntilIso);
    const ms = until.getTime() - Date.now();
    if (ms <= 0) return "Expirado";
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins} min restantes`;
    const hours = Math.floor(mins / 60);
    if (hours < 48) return `${hours}h ${mins % 60}min restantes`;
    return `${Math.floor(hours / 24)}d ${hours % 24}h restantes`;
  } catch {
    return "";
  }
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LeadVillaHoldCard({ leadId, leadStatus, onChanged, testid = "lead-villa-hold-card" }) {
  const [holds, setHolds] = useState([]);
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null);

  // Form state
  const [selectedVillaId, setSelectedVillaId] = useState("");
  const [durationKey, setDurationKey] = useState("48h");
  const [customUntil, setCustomUntil] = useState("");
  const [note, setNote] = useState("");

  const activeHolds = useMemo(
    () => holds.filter((h) => h.status === "active"),
    [holds],
  );
  // Local toggle for the "add another" form when there's already one or more
  // active holds. When there are none, the form is shown by default.
  const [addingHold, setAddingHold] = useState(false);
  const showForm = activeHolds.length === 0 || addingHold;

  const load = async () => {
    if (!leadId) return;
    setLoading(true); setError("");
    try {
      const [hRes, vRes] = await Promise.all([
        api.get(`/villa-holds?lead_id=${encodeURIComponent(leadId)}`),
        villas.length === 0
          ? api.get("/villas").catch(() => ({ data: [] }))
          : Promise.resolve({ data: villas }),
      ]);
      setHolds(Array.isArray(hRes.data) ? hRes.data : []);
      if (Array.isArray(vRes.data) && villas.length === 0) setVillas(vRes.data);
    } catch (e) { setError(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const villaName = (id) => {
    const v = villas.find((x) => x.villa_id === id);
    return v ? (v.name || v.villa_id) : id;
  };

  const computeHoldUntilIso = () => {
    if (durationKey === "custom") {
      if (!customUntil) return null;
      const d = new Date(customUntil);
      if (isNaN(d.getTime())) return null;
      return d.toISOString();
    }
    const preset = DURATION_PRESETS.find((p) => p.key === durationKey);
    if (!preset || preset.hours == null) return null;
    return new Date(Date.now() + preset.hours * 3600 * 1000).toISOString();
  };

  const minCustom = useMemo(() => {
    const d = new Date(Date.now() + 30 * 60 * 1000); // +30min from now
    return toLocalDateTimeInput(d);
  }, []);
  const maxCustom = useMemo(() => {
    const d = new Date(Date.now() + 30 * 24 * 3600 * 1000); // +30 days
    return toLocalDateTimeInput(d);
  }, []);

  const create = async () => {
    if (!leadId) return;
    setConflict(null); setError("");
    if (!selectedVillaId) { setError("Selecciona una villa."); return; }
    const holdUntil = computeHoldUntilIso();
    if (!holdUntil) { setError("Indica una fecha/hora válida para el hold."); return; }
    setBusy(true);
    try {
      await api.post("/villa-holds", {
        lead_id: leadId,
        villa_id: selectedVillaId,
        hold_until: holdUntil,
        note: note.trim() || null,
        // Only flip lead status on the *first* hold so subsequent holds
        // don't re-emit a noisy status change. Backend also guards against
        // re-entry, but being explicit keeps intent obvious.
        set_lead_status: activeHolds.length === 0,
      });
      setSelectedVillaId(""); setNote(""); setDurationKey("48h"); setCustomUntil("");
      setAddingHold(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "villa_hold_conflict") {
        setConflict(detail);
      } else {
        setError(formatError(e));
      }
    } finally { setBusy(false); }
  };

  const release = async (holdId) => {
    if (!holdId) return;
    setBusy(true); setError("");
    try {
      await api.patch(`/villa-holds/${holdId}/release`, { reason: null });
      await load();
      onChanged && onChanged();
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  const statusLabel = (s) => ({
    active: "Hold activo",
    released: "Hold liberado",
    expired: "Hold expirado",
    converted: "Hold convertido",
  })[s] || s;

  const statusTone = (s) => ({
    active:    "bg-amber-50 text-amber-900 border-amber-200",
    released:  "bg-stone-50 text-stone-700 border-stone-200",
    expired:   "bg-stone-100 text-stone-600 border-stone-200",
    converted: "bg-emerald-50 text-emerald-800 border-emerald-200",
  })[s] || "bg-stone-50 text-stone-700 border-stone-200";

  return (
    <div className="dv-card space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-stone-800 inline-flex items-center gap-1.5">
          <House size={14} weight="regular" /> Villa hold
        </h3>
        {activeHolds.length > 0 && (
          <span className="text-[10px] uppercase tracking-widest text-amber-700">
            {activeHolds.length === 1 ? "Activo" : `${activeHolds.length} activos`}
          </span>
        )}
      </div>

      {error && (
        <div className="text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-700" data-testid="lead-villa-hold-error">
          {error}
        </div>
      )}

      {conflict && (
        <div
          className="text-xs px-2 py-2 rounded border bg-amber-50 border-amber-200 text-amber-900 inline-flex items-start gap-1.5"
          data-testid="lead-villa-hold-conflict"
        >
          <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />
          <div>
            {conflict.message || "Esta villa ya está en hold para otro lead."}
            {conflict.existing_hold_until && (
              <div className="text-[11px] text-amber-800 mt-0.5">
                Hasta: {formatDate(conflict.existing_hold_until)}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <div className="text-xs text-stone-400">Cargando…</div>}

      {/* Active holds — list (zero or more) */}
      {!loading && activeHolds.length > 0 && (
        <div className="space-y-2" data-testid="lead-villa-hold-active-list">
          {activeHolds.map((h) => (
            <div
              key={h.hold_id}
              className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2"
              data-testid={`lead-villa-hold-active-${h.hold_id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-stone-800 truncate">{villaName(h.villa_id)}</div>
                  <div className="text-[11px] text-stone-600 inline-flex items-center gap-1 mt-0.5">
                    <Clock size={11} weight="regular" />
                    Hasta {formatDate(h.hold_until)}
                  </div>
                  <div className="text-[11px] text-amber-800 mt-0.5">
                    {formatRemaining(h.hold_until)}
                  </div>
                  {h.note && (
                    <div className="text-[11px] text-stone-700 mt-1 whitespace-pre-wrap">
                      “{h.note}”
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => release(h.hold_id)}
                  disabled={busy}
                  data-testid={`lead-villa-hold-release-${h.hold_id}`}
                  className="text-xs px-2.5 py-1 rounded border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <X size={11} weight="bold" /> Liberar hold
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add another villa CTA — only shown when at least one hold is active
          and the form is collapsed. Keeps the card tidy when N grows. */}
      {!loading && activeHolds.length > 0 && !addingHold && (
        <button
          type="button"
          onClick={() => setAddingHold(true)}
          disabled={busy}
          data-testid="lead-villa-hold-add-btn"
          className="text-xs px-2.5 py-1 rounded border border-stone-300 text-stone-700 hover:bg-stone-50 inline-flex items-center gap-1"
        >
          <Plus size={11} weight="bold" /> Añadir otra villa en hold
        </button>
      )}

      {/* Create form — visible by default when there are no active holds,
          or after the user clicks "Añadir otra villa en hold". */}
      {!loading && showForm && (
        <div className="space-y-2">
          <div className="text-[11px] text-stone-500">
            Pon temporalmente una villa en hold para este lead. No afecta el estado real de la villa.
          </div>
          <label className="block">
            <span className="text-[11px] text-stone-500">Villa</span>
            <select
              className="dv-input text-sm mt-0.5 w-full"
              value={selectedVillaId}
              onChange={(e) => { setSelectedVillaId(e.target.value); setConflict(null); }}
              disabled={busy}
              data-testid="lead-villa-hold-villa"
            >
              <option value="">— Selecciona una villa —</option>
              {villas
                .filter((v) => v.status !== "sold")
                .filter((v) => !activeHolds.some((h) => h.villa_id === v.villa_id))
                .map((v) => (
                  <option key={v.villa_id} value={v.villa_id}>
                    {v.name || v.villa_id}{v.status === "reserved" ? " (reservada)" : ""}
                  </option>
                ))}
            </select>
          </label>

          <div>
            <span className="text-[11px] text-stone-500">Duración</span>
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {DURATION_PRESETS.map((p) => {
                const active = durationKey === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setDurationKey(p.key)}
                    disabled={busy}
                    data-testid={`lead-villa-hold-duration-${p.key}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${
                      active
                        ? "border-stone-900 bg-stone-900 text-white"
                        : "border-stone-200 bg-white text-stone-700 hover:border-stone-500"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {durationKey === "custom" && (
            <label className="block">
              <span className="text-[11px] text-stone-500">Fecha/hora exacta</span>
              <input
                type="datetime-local"
                className="dv-input text-sm mt-0.5 w-full"
                value={customUntil}
                onChange={(e) => setCustomUntil(e.target.value)}
                min={minCustom}
                max={maxCustom}
                disabled={busy}
                data-testid="lead-villa-hold-custom"
              />
            </label>
          )}

          <label className="block">
            <span className="text-[11px] text-stone-500">Motivo / nota (opcional)</span>
            <textarea
              className="dv-input text-sm mt-0.5 w-full"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={busy}
              placeholder="Ej: Esperando respuesta del cliente sobre Vistas 02."
              data-testid="lead-villa-hold-note"
            />
          </label>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={create}
              disabled={busy || !selectedVillaId}
              data-testid="lead-villa-hold-create-btn"
              className="dv-btn dv-btn-primary inline-flex items-center gap-1 text-sm disabled:opacity-50"
            >
              <Plus size={12} weight="bold" /> Poner villa en hold
            </button>
            {activeHolds.length > 0 && addingHold && (
              <button
                type="button"
                onClick={() => {
                  setAddingHold(false);
                  setSelectedVillaId(""); setNote("");
                  setDurationKey("48h"); setCustomUntil("");
                  setConflict(null); setError("");
                }}
                disabled={busy}
                data-testid="lead-villa-hold-cancel-btn"
                className="text-xs text-stone-500 hover:text-stone-800"
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {/* History (non-active rows) */}
      {!loading && holds.filter((h) => h.status !== "active").length > 0 && (
        <details className="text-xs text-stone-600 mt-1" data-testid="lead-villa-hold-history">
          <summary className="cursor-pointer select-none text-stone-500 hover:text-stone-700">
            Historial ({holds.filter((h) => h.status !== "active").length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {holds.filter((h) => h.status !== "active").map((h) => (
              <li
                key={h.hold_id}
                className={`px-2 py-1.5 rounded border text-[11px] ${statusTone(h.status)}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{villaName(h.villa_id)}</span>
                  <span className="uppercase tracking-wider">{statusLabel(h.status)}</span>
                </div>
                <div className="text-[10px] mt-0.5 opacity-80">
                  Hasta {formatDate(h.hold_until)}
                  {h.released_at && ` · liberado ${formatDate(h.released_at)}`}
                  {h.converted_at && ` · convertido ${formatDate(h.converted_at)}`}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
